// # Product Revenue Component
import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import './bnct_product_revenue.css';

const ProductRevenue = ({ revenueData, member }) => {
  // # Check if data is available
  if (!revenueData) return <div className="bnct_loading">Loading product revenue data...</div>;

  // # Determine if this is a project budget account
  const isProjectBudget = member && member.id === 'project-budget';
  
  // # Get the appropriate product data based on account type - use stored values directly
  const productData = isProjectBudget ? 
    (revenueData.budgetByProduct || {}) : 
    (revenueData.revenueByProduct || {});

  return (
    <div className="bnct_product-container">
      {/* # Product Revenue Chart */}
      <div className="bnct_product-chart-container">
        <h3>{isProjectBudget ? 'Budget Allocation by Product Type' : 'Net Revenue by Product Type'}</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={Object.entries(productData).map(([product, amount]) => ({
            product,
            amount
          }))}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="product" />
            <YAxis />
            <Tooltip formatter={(value) => `E£ ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
            <Legend />
            <Bar dataKey="amount" fill="#8884d8" name={isProjectBudget ? "Budget (EGP)" : "Revenue (EGP)"} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* # Product Share Percentages - Only show for team members */}
      {!isProjectBudget && (
        <div className="bnct_product-shares">
          <h3>Revenue Share Percentages</h3>
          <div className="bnct_share-grid">
            {/* # Library Subscriptions */}
            <div className="bnct_share-card">
              <h4>Library Subscriptions</h4>
              <p>28% of net revenue</p>
              <div className="bnct_share-details">
                <ul>
                  <li>Elhadidy: 28%</li>
                  <li>Hussein: 20%</li>
                  <li>Ayman: 20%</li>
                  <li>Hossam: 20%</li>
                  <li>Nabil: 12%</li>
                </ul>
              </div>
            </div>
            
            {/* # Premium Subscriptions */}
            <div className="bnct_share-card">
              <h4>Premium Subscriptions</h4>
              <p>22.125% of net revenue</p>
              <div className="bnct_share-details">
                <ul>
                  <li>Elhadidy: 22.125%</li>
                  <li>Hussein: 22.125%</li>
                  <li>Ayman: 22.125%</li>
                  <li>Hossam: 22.125%</li>
                  <li>Nabil: 11.5%</li>
                </ul>
              </div>
            </div>
            
            {/* # Mentorship (Egyptian) */}
            <div className="bnct_share-card">
              <h4>Mentorship (Egyptian)</h4>
              <p>50% as mentor, 12.5% as team member</p>
              <div className="bnct_share-details">
                <ul>
                  <li>Assigned Mentor: 50%</li>
                  <li>Other Team Members: 12.5% each</li>
                </ul>
              </div>
            </div>
            
            {/* # Mentorship (International) */}
            <div className="bnct_share-card">
              <h4>Mentorship (International)</h4>
              <p>33% as mentor, 16.75% as team member</p>
              <div className="bnct_share-details">
                <ul>
                  <li>Assigned Mentor: 33%</li>
                  <li>Other Team Members: 16.75% each</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* # Budget Allocation Info - Only show for project budget */}
      {isProjectBudget && (
        <div className="bnct_product-shares">
          <h3>Budget Allocation Information</h3>
          <div className="bnct_share-grid">
            <div className="bnct_share-card">
              <h4>Budget Source</h4>
              <p>5% of gross revenue from all products</p>
              <div className="bnct_share-details">
                <p>The project budget receives 5% of the gross revenue from all product sales before team member shares are calculated.</p>
              </div>
            </div>
            
            <div className="bnct_share-card">
              <h4>Budget Usage</h4>
              <p>Expenses, investments, and project costs</p>
              <div className="bnct_share-details">
                <p>The budget is used for operational expenses, marketing costs, platform investments, and other project-related expenditures.</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* # Product Revenue Table - Use stored values directly */}
      <div className="bnct_product-table-container">
        <h3>{isProjectBudget ? 'Budget Allocation Breakdown' : 'Product Total Revenue Breakdown'}</h3>
        <div className="bnct_product-table-wrapper">
          <table className="bnct_product-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>{isProjectBudget ? 'Budget Amount' : 'Total Revenue'}</th>
                <th>% of Total</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(productData)
                .sort(([, amountA], [, amountB]) => amountB - amountA)
                .map(([product, amount]) => (
                  <tr key={product}>
                    <td>{product}</td>
                    <td>E£ {amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td>{((amount / (revenueData.totalRevenue || 1)) * 100).toFixed(2)}%</td>
                  </tr>
                ))}
              <tr className="bnct_product-total">
                <td>Total</td>
                <td>E£ {(revenueData.totalRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td>100%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProductRevenue; 